// src/UserContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios'; // Importar axios para chamadas API
import { useAuthContext } from './context/AuthContext'; // Importar AuthContext
import { useUnitContext } from './context/UnitContext';
import { billingOnly } from './services/institutionAccess';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
    const unitContext = useUnitContext();
    const { authState } = useAuthContext(); // Consumir AuthContext para obter o status do onboarding

// 'Representante Legal', 'Médico', 'Recepção', 'Técnico'
const [dynamicAccessConfig, setDynamicAccessConfig] = useState({});
const [accessConfigLoading, setIsLoadingAccessConfig] = useState(true);
const [loadedAccessScope,setLoadedAccessScope]=useState(null); // Manter true como estado inicial é importante
const [currentUserRole, _setCurrentUserRole] = useState(null);
const restricted = billingOnly(authState);
const accessEnabled = Boolean(authState.isAuthenticated && authState.roles?.includes('clinica') && authState.onboarding?.complete && !restricted);
const accessScope = `${authState.user?.id}:${authState.entidade?.id}:${currentUserRole}`;
// A newly authenticated profile must never reuse a previous profile's permissions,
// nor be treated as empty during the render before its fetch effect starts.
const isLoadingAccessConfig = accessEnabled && (!currentUserRole || accessConfigLoading || loadedAccessScope !== accessScope);
 // Inicializa como null

const setCurrentUserRole = useCallback((role) => {
    // O localStorage não é mais usado aqui para currentUserRole
    _setCurrentUserRole(role);
}, []);
 
    // O useEffect que continha o MOCK para fetchAccessConfig foi removido.
    // A lógica de busca da configuração de acesso agora está centralizada
    // no useEffect abaixo, que faz a chamada real à API.

    // Este useEffect é para a chamada real à API
    useEffect(() => {
        let live=true;
        // A chamada à API só deve acontecer se o usuário for do tipo 'clinica',
        // estiver autenticado, com onboarding completo e com a role definida.
        if (
            authState.isAuthenticated &&
            !restricted &&
            authState.roles?.includes('clinica') &&
            currentUserRole &&
            authState.onboarding?.complete
        ) {
            setIsLoadingAccessConfig(true);

            axios.get('/api/pages-with-tabs')
                .then(response => {
                    if(live)setDynamicAccessConfig(response.data || {});
                })
                .catch(error => {
                    if(!live)return;
                    console.error("Erro ao buscar configuração de acesso da API:", error);
                    setDynamicAccessConfig({}); // Limpa a configuração em caso de erro
                })
                .finally(() => {
                    if(live){setLoadedAccessScope(accessScope);setIsLoadingAccessConfig(false);}
                });
        } else {
            // Se qualquer uma das condições não for atendida, limpa a configuração e para o loading.
            setDynamicAccessConfig({});
            setIsLoadingAccessConfig(false);
        }
        return ()=>{live=false;};
    }, [currentUserRole, authState.onboarding?.complete, authState.isAuthenticated, authState.roles, accessScope, restricted]);

    // Função que será exposta pelo contexto.
    // Ela usa 'currentUserRole' do estado do UserProvider se 'roleOverride' não for fornecido.
    const getPermittedInternalTabs = (pageKey, roleOverride) => {
        const roleToUse = roleOverride || currentUserRole;
        if (unitContext?.navigationContext === 'hospitalar' && pageKey === 'consultorio'
            && !dynamicAccessConfig.hospitalar?.pacientes?.roles?.includes(roleToUse)) return [];
        const moduleConfigFromApi = dynamicAccessConfig[pageKey];


        if (!moduleConfigFromApi || !roleToUse || isLoadingAccessConfig) {
            return [];
        }

        const permittedTabs = [];
        for (const itemKey in moduleConfigFromApi) {
            const itemConfig = moduleConfigFromApi[itemKey];
            // Verifica se é uma aba (tem 'key', 'title', 'roles') e não é o 'header', 'key' ou 'title' do módulo principal
            if (itemKey !== 'header' && itemKey !== 'key' && itemKey !== 'title' &&
                itemConfig && typeof itemConfig === 'object' && itemConfig.key && itemConfig.title && Array.isArray(itemConfig.roles)) {
                if (itemConfig.roles.includes(roleToUse)) {
                    permittedTabs.push({ key: itemConfig.key, title: itemConfig.title });
                }
            }
        }
        return permittedTabs;
    };

    // Nova função para obter as abas permitidas para o header (Navbar)
    const getPermittedHeaderTabs = (entidade, roleOverride) => {
      const roleToUse = roleOverride || currentUserRole;
      // Adicionada verificação para dynamicAccessConfig vazio
      if (!roleToUse || !entidade || isLoadingAccessConfig || Object.keys(dynamicAccessConfig).length === 0) {
        return [];
      }

      return Object.values(dynamicAccessConfig).filter(moduleConfig => {
        // Verifica se a configuração do header existe e se a role é permitida
        const roleAllowed = moduleConfig.header?.roles?.includes(roleToUse);
        if (!roleAllowed) {
            return false; // Se o perfil não tem acesso ao módulo, não continua
        }
        if (unitContext?.navigationContext === 'hospitalar' && moduleConfig.key === 'consultorio') {
            return Boolean(dynamicAccessConfig.hospitalar?.pacientes?.roles?.includes(roleToUse)
                && moduleConfig.pacientes?.roles?.includes(roleToUse));
        }

        // Se não tem chave no módulo (improvável para abas de header configuradas via API), considera ativo
        if (!moduleConfig.key) {
            return true;
        }

        // Caso especial: Onboarding completo e modulos_ativos da entidade é null.
        // Neste cenário, se o perfil tem acesso (verificado acima), o módulo é considerado ativo.
        if (authState.onboarding?.complete && entidade.modulos_ativos === null) {
            return true;
        }

        // Lógica padrão se o onboarding não estiver completo ou se modulos_ativos estiver preenchido (não nulo)
        const isModuleRequiredByItsConfig = moduleConfig.header?.moduleRequired;

        if (isModuleRequiredByItsConfig === true) {
            // Se o módulo é explicitamente marcado como 'requerido' pela sua própria configuração,
            // ele DEVE estar na lista de modulos_ativos da entidade (que neste ponto não é null).
            return entidade.modulos_ativos?.includes(moduleConfig.key) ?? false;
        } else {
            // Se o módulo NÃO é explicitamente 'requerido' (moduleRequired é false ou undefined),
            // ele é considerado ativo (desde que o perfil tenha acesso, já verificado).
            return true;
        }
      }).map(moduleConfig => ({ // Mapeia para o formato esperado pelo Navbar
          key: moduleConfig.key,
          title: moduleConfig.title,
          ...(moduleConfig.key === 'hospitalar' ? {
            hospitalarActions: Object.entries(moduleConfig).filter(([,value]) => value?.roles?.includes(roleToUse)).map(([key]) => key),
            hospitalarManager: moduleConfig.manager === true,
          } : {})
      }));
    };

  return (
    <UserContext.Provider value={{
      currentUserRole,
      setCurrentUserRole,
      getPermittedInternalTabs,
      getPermittedHeaderTabs,
      dynamicAccessConfig, // Pode ser útil para outros componentes
      isLoadingAccessConfig // Para que outros componentes saibam se a config está carregando
    }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
